import {Component, OnInit, signal, ChangeDetectorRef, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {Toast} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {TranslatePipe} from '@ngx-translate/core';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {PrimeNG} from 'primeng/config';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {Bid, FieldService} from "@6grain/masofa-monolith-api-client/crop-monitoring";
import {TranslateMessageService} from "@services/translate-message";
import {GroundSurvey} from "@app/pages/crop-monitoring/components/request-add-page/forms/ground-surver/ground-survey";
import {BackButton} from "@components/ui/back-button/back-button";
import {ButtonDirective} from "primeng/button";
import {DialogComponent} from "@components/dialog";
import {Uav} from "@app/pages/crop-monitoring/components/request-add-page/forms/uav/uav";
import {Loading} from '@components/ui/loading/loading';
import {BidStateType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidStateType';
import {BidUpdateViewModel} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidUpdateViewModel';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {resolveBidWorkType} from '@shared/data/bid-work-type.util';

@Component({
  selector: 'app-request-add-page',
  standalone: true,
  imports: [
    CommonModule,
    BackButton,
    GroundSurvey,
    Toast,
    ButtonDirective,
    DialogComponent,
    TranslatePipe,
    Uav,
    Loading,
  ],
  providers: [
    {provide: MessageService, useClass: TranslateMessageService}
  ],

  templateUrl: './request-add-page.html',
  styleUrl: './request-add-page.scss'
})
export class RequestAddPageComponent implements OnInit {

  @ViewChild(Toast) toast!: Toast;

  requestType = signal<'ground' | 'limiting'>('ground');
  isMapMode = signal(false);
  isSaving = signal(false);
  isCanceling = signal(false);
  isLoading = signal(false);

  bid: Bid | any = {}
  showConfirmCancelModal: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public regionService: RegionService,
    private bidService: BidService,
    private fieldService: FieldService,
    private messageService: MessageService,
    public primeng: PrimeNG,
    private cdr: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {


    // Check if we're in edit mode
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.loadRequestForEdit(params['id']);
      } else {
        setTimeout(() => {

          this.cdr.detectChanges();
        }, 100);
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['type'] === 'limiting') {
        this.requestType.set('limiting');
        this.isMapMode.set(true);
      } else {
        this.isMapMode.set(true);
      }
      if (params['fieldId']) {
        this.bid.fieldId = params['fieldId'];
        this.loadBidFromField();
      }
    });
  }

  saveRequest() {

    const payload = this.createBidPayload();

    this.isSaving.set(true);
    if (this.bid?.id) {
      this.bidService.cropMonitoringBidCustomUpdatePut(payload).subscribe({
        next: (result) => {
          this.requestMessage('success', 'Success', 'requestUpdatedSuccessfully');
          this.isSaving.set(false);
          // setTimeout(() => this.backToList(), 150);
        },
        error: (httpError:HttpErrorResponse) => {
          console.error('Error updating request:', httpError);
          this.requestMessage('error', 'Error', httpError.error);
          this.isSaving.set(false);
        }
      });
    } else {
      // Create new request
      this.bidService.cropMonitoringBidCustomCreatePost(payload).subscribe({
        next: (id) => {
          this.requestMessage('success', 'Success', 'requestCreatedSuccessfully');
          this.isSaving.set(false);

          // setTimeout(() => this.backToList(), 150);
          if (id){
            this.router.navigate(['/crop-monitoring/requests/edit/'+id], );

          }
        },
        error: (httpError:HttpErrorResponse) => {
          console.error('Error creating request:', httpError);
          this.requestMessage('error', 'Error', httpError.error);
          this.isSaving.set(false);
        }
      });
    }
  }


  cancelBid() {
    // this.backToList();
    const cancelParam: any = {
      ...this.createBidPayload(),
      bidState: BidStateType.NUMBER_5
    }

    this.isCanceling.set(true);

    this.bidService.cropMonitoringBidUpdatePut(cancelParam).subscribe({
      next: () => {
        this.requestMessage('success', 'Success', 'requestCancelSuccessfully');
        this.isCanceling.set(false);
        setTimeout(() => this.backToList(), 150);
      },
      error: (error) => {
        console.error('Error updating request:', error);
        // this.requestMessage('error', 'Error', 'requestCancelFailed');
        this.isCanceling.set(false);
      }
    });
  }

  backToList() {
    this.router.navigate(['/crop-monitoring/requests']);
  }

  requestMessage(severity: string, summary: string, detail: string): void {
    try {
      if (this.toast && typeof (this.toast as any).show === 'function') {
        (this.toast as any).show({severity, summary, detail, life: 3000});
        return;
      }
    } catch (e) {
      // ignore
    }

    // Fallback vers MessageService
    this.messageService.add({
      severity,
      summary,
      detail,
      life: 3000
    })
  }

  loadRequestForEdit(id: string) {
    this.isLoading.set(true)
    this.bidService.cropMonitoringBidGetByIdIdGet(id).subscribe({
      next: (bid) => {
        this.bid = bid as any;
        this.isLoading.set(false);

      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }


  private createBidPayload(): BidUpdateViewModel {
    const cloneBid: BidUpdateViewModel = {
      parentId: this.bid.parentId,
      bidTypeId: this.bid.bidTypeId,
      foremanId: this.bid.foremanId,
      workerId: this.bid.workerId,
      startDate: this.bid.startDate,
      deadlineDate: this.bid.deadlineDate,
      endDate: this.bid.endDate,
      fieldId: this.bid.fieldId,
      regionId: this.bid.regionId,
      cropId: this.bid.cropId,
      varietyId: this.bid.varietyId,
      comment: this.bid.comment,
      description: this.bid.description,
      lat: this.bid.lat,
      lng: this.bid.lng,
      number: this.bid.number,
      fieldPlantingDate: this.bid.fieldPlantingDate,
      customer: this.bid.customer,
      fileResultId: this.bid.fileResultId,
      bidTemplateId: this.bid.bidTemplateId,
      isUnvalidBid: this.bid.isUnvalidBid,
      bidState: this.getBidState,
      status: this.bid.status,
      polygon: this.bid.polygon as any,
      publish: this.bid.publish,
      archive: this.bid.archive,
      bidWorkType: resolveBidWorkType(this.bid.bidWorkType),
    } as BidUpdateViewModel & { bidWorkType?: number };

    if (this.bid?.id) {
      cloneBid['id'] = this.bid.id;
    }

    this.bid['archive'] = cloneBid.archive;
    this.bid['publish'] = cloneBid.publish;

    return cloneBid;
  }

  get getBidState(): BidStateType {

    if (this.bid.bidState !== BidStateType.NUMBER_5) {
      if (this.bid?.publish && !this.bid?.archive) {
        return BidStateType.NUMBER_1;
      }
    }
    return this.bid?.bidState;

  }

  loadBidFromField() {
    if (this.bid?.fieldId) {
      this.isLoading.set(true)
      this.fieldService.cropMonitoringFieldGetByIdIdGet(this.bid.fieldId).subscribe({
        next: (field) => {
          if (field.centerX){
            this.bid.lat = field.centerX;
          }
          if (field.centerY){
            this.bid.lng = field.centerY;
          }
          this.bid.regionId = field.regionId;
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }

  }
}
